import type { OpeningImportItem, OpeningRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatQty } from '@hq/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Empty, Form, Input, Modal, Popconfirm, Select, Space, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMemo, useState } from 'react';
import { hq } from '../api/client';

interface ImportForm {
  warehouseId: string;
  ownerType: string;
  importBatchNo: string;
  remark?: string;
  csv: string;
}

const CSV_HEADER = 'materialCode,batchNo,expiryDate,qty,price';

/**
 * 期初建账（P0 上线前置）：粘贴/粘贴 Excel 复制出的 CSV 导入第三方现有库存，
 * 生成 DRAFT 批次 → 复核 → 过账（写库存与流水）→ 可整批回滚。
 */
export default function OpeningPage() {
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<ImportForm>();
  const [status, setStatus] = useState<string>('');
  const queryClient = useQueryClient();

  const warehouses = useQuery({
    queryKey: ['org-structure', 'warehouse', 'options'],
    queryFn: () => hq.orgStructure.warehouse.list()
  });

  const list = useQuery({
    queryKey: ['opening', status],
    queryFn: () => hq.opening.list({ status: status || undefined, size: 200 })
  });

  const importMutation = useMutation({
    mutationFn: (values: ImportForm) => {
      const items = parseCsv(values.csv);
      return hq.opening.import({
        warehouseId: Number(values.warehouseId),
        ownerType: values.ownerType,
        importBatchNo: values.importBatchNo || undefined,
        remark: values.remark,
        items
      });
    },
    onSuccess: (result) => {
      message.success(`导入成功：批次 ${result.importBatchNo}，${result.itemCount} 行，金额 ${formatAmount(result.totalAmount)}`);
      setOpen(false);
      form.resetFields();
      void queryClient.invalidateQueries({ queryKey: ['opening'] });
    }
  });

  const postMutation = useMutation({
    mutationFn: (batchNo: string) => hq.opening.post(batchNo),
    onSuccess: () => {
      message.success('过账完成：库存与流水已写入');
      void queryClient.invalidateQueries({ queryKey: ['opening'] });
    }
  });

  const rollbackMutation = useMutation({
    mutationFn: (batchNo: string) => hq.opening.rollback(batchNo),
    onSuccess: (result) => {
      message.success(`回滚完成：删除草稿 ${result.deletedDrafts} 行，冲销已过账 ${result.reversedPosted} 行`);
      void queryClient.invalidateQueries({ queryKey: ['opening'] });
    }
  });

  const rows = list.data ?? [];

  const batches = useMemo(() => {
    const map = new Map<string, { batchNo: string; status: string; count: number; amount: number }>();
    for (const row of rows) {
      const batchNo = row.importBatchNo || '—';
      const current = map.get(batchNo) ?? { batchNo, status: row.status, count: 0, amount: 0 };
      current.count += 1;
      current.amount += row.amount;
      if (row.status === 'POSTED') current.status = 'POSTED';
      map.set(batchNo, current);
    }
    return [...map.values()];
  }, [rows]);

  const columns: ColumnsType<OpeningRow> = [
    { title: '物资编码', dataIndex: 'materialCode', width: 130 },
    { title: '品名', dataIndex: 'materialName', width: 180 },
    { title: '规格', dataIndex: 'spec', width: 140 },
    { title: '批号', dataIndex: 'batchNo', width: 120 },
    {
      title: '效期',
      dataIndex: 'expiryDate',
      width: 120,
      render: (value: string | null) => value?.slice(0, 10) ?? '—'
    },
    { title: '数量', dataIndex: 'qty', width: 110, align: 'right', render: (value: number) => formatQty(value) },
    { title: '单价', dataIndex: 'price', width: 110, align: 'right', render: (value: number) => value.toFixed(2) },
    { title: '金额', dataIndex: 'amount', width: 130, align: 'right', render: (value: number) => formatAmount(value) },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (value: string) =>
        value === 'POSTED' ? <Tag color="green">已过账</Tag> : <Tag color="gold">草稿</Tag>
    },
    { title: '批次', dataIndex: 'importBatchNo', width: 160 }
  ];

  const error = importMutation.error instanceof ApiError
    ? importMutation.error.friendlyMessage
    : importMutation.error ? String(importMutation.error) : null;

  return (
    <>
      <Card
        className="page-card"
        title="期初建账"
        extra={
          <Space>
            <Select
              allowClear
              placeholder="状态筛选"
              style={{ width: 140 }}
              value={status || undefined}
              onChange={(value) => setStatus(value ?? '')}
              options={[
                { value: 'DRAFT', label: '草稿' },
                { value: 'POSTED', label: '已过账' }
              ]}
            />
            <Button type="primary" onClick={() => setOpen(true)}>导入期初库存</Button>
          </Space>
        }
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="上线前置：第三方清点库存按批次导入为草稿，复核无误后过账；过账后可整批回滚（反向流水）。"
        />

        {batches.length > 0 ? (
          <Space wrap style={{ marginBottom: 12 }}>
            {batches.map((batch) => (
              <Card key={batch.batchNo} size="small" style={{ minWidth: 230 }}>
                <Typography.Text strong>{batch.batchNo}</Typography.Text>
                <div>
                  <Tag color={batch.status === 'POSTED' ? 'green' : 'gold'}>
                    {batch.status === 'POSTED' ? '已过账' : '草稿'}
                  </Tag>
                  {batch.count} 行 / {formatAmount(batch.amount)}
                </div>
                <Space style={{ marginTop: 8 }}>
                  {batch.status === 'DRAFT' ? (
                    <Popconfirm
                      title="过账后写入库存与流水，确定继续？"
                      onConfirm={() => postMutation.mutate(batch.batchNo)}
                    >
                      <Button size="small" type="primary" loading={postMutation.isPending}>过账</Button>
                    </Popconfirm>
                  ) : null}
                  <Popconfirm
                    title="回滚将删除草稿或冲销已过账库存，确定继续？"
                    onConfirm={() => rollbackMutation.mutate(batch.batchNo)}
                  >
                    <Button size="small" danger loading={rollbackMutation.isPending}>回滚</Button>
                  </Popconfirm>
                </Space>
              </Card>
            ))}
          </Space>
        ) : null}

        {list.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            message="期初数据加载失败"
            description={list.error instanceof ApiError ? list.error.friendlyMessage : String(list.error)}
          />
        ) : null}

        <Table<OpeningRow>
          rowKey="id"
          size="middle"
          columns={columns}
          dataSource={rows}
          loading={list.isPending}
          pagination={false}
          locale={{ emptyText: <Empty description="暂无期初建账数据，可先导入第三方清点结果" /> }}
        />
      </Card>

      <Modal
        open={open}
        title="导入期初库存"
        width={720}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={importMutation.isPending}
        destroyOnClose
      >
        {error ? <Alert type="error" showIcon style={{ marginBottom: 12 }} message="导入失败" description={error} /> : null}
        <Form<ImportForm>
          form={form}
          layout="vertical"
          onFinish={(values) => importMutation.mutate(values)}
          initialValues={{ ownerType: 'HOSPITAL' }}
        >
          <Form.Item name="warehouseId" label="收货仓库" rules={[{ required: true, message: '请选择仓库' }]}>
            <Select
              placeholder="请选择仓库"
              options={(warehouses.data ?? []).map((item) => ({
                value: item.id,
                label: item.whName ?? item.whCode ?? String(item.id)
              }))}
            />
          </Form.Item>
          <Space size={16} style={{ display: 'flex' }}>
            <Form.Item name="ownerType" label="库存归属" style={{ flex: 1 }}>
              <Select
                options={[
                  { value: 'HOSPITAL', label: '院内库存' },
                  { value: 'SUPPLIER', label: '供应商寄售' },
                  { value: 'THIRD_PARTY', label: '第三方寄存' }
                ]}
              />
            </Form.Item>
            <Form.Item name="importBatchNo" label="导入批次号（留空自动生成）" style={{ flex: 1 }}>
              <Input placeholder="如 OPEN-20261030" />
            </Form.Item>
          </Space>
          <Form.Item
            name="csv"
            label="CSV 数据（可从 Excel 直接复制）"
            rules={[{ required: true, message: '请粘贴 CSV 数据' }]}
            extra={`首行表头：${CSV_HEADER}；materialCode 必须对应物资主数据编码，expiryDate 可留空`}
          >
            <Input.TextArea rows={10} placeholder={`${CSV_HEADER}\nM001,20260101,2027-01-01,10,12.50`} />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input placeholder="如：第三方清点结果，2026-10-30 复核" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

/** 解析粘贴的 CSV：首行有表头则跳过；空行忽略；数值非法该行报错。 */
function parseCsv(text: string): OpeningImportItem[] {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length === 0) throw new Error('CSV 不能为空');

  const start = /^material(Code|_code)/i.test(lines[0] ?? '') ? 1 : 0;
  if (lines.length <= start) throw new Error('CSV 缺少数据行');

  return lines.slice(start).map((line, index) => {
    const cells = line.split(',').map((cell) => cell.trim());
    const [materialCode, batchNo, expiryDate, qtyRaw, priceRaw] = cells;
    const qty = Number(qtyRaw);
    const price = Number(priceRaw);
    if (!materialCode) throw new Error(`第 ${index + start + 1} 行缺少物资编码`);
    if (!Number.isFinite(qty) || qty <= 0) throw new Error(`第 ${index + start + 1} 行数量非法`);
    if (!Number.isFinite(price) || price < 0) throw new Error(`第 ${index + start + 1} 行单价非法`);

    return {
      materialCode,
      batchNo: batchNo || undefined,
      expiryDate: expiryDate || undefined,
      qty,
      price
    };
  });
}
